/**
 * Blóðprufubeiðni: orders the todo's blood tests from Sameind via Mercury's
 * BloodTestWorkspace. Without a læknanúmer on file only the one-time
 * læknanúmer prompt is shown; while impersonating, ordering is blocked.
 * Outside the ordering gate the task is a plain text request (BloodTestRequestEditor).
 *
 * LOCKED mode: the caller already knows the patient (session/todo scoped) —
 * shows a read-only "Pantað fyrir" block, no kennitala input.
 * LOOKUP mode: no patient known — shows the kennitala field and looks the
 * value up as soon as it's a valid 10-digit kennitala.
 */

import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useAuth } from '@/contexts/AuthContext';
import { LaeknanumerPrompt } from '@/features/laeknanumer/components/LaeknanumerPrompt';
import { normalizeKennitala, usePatientLookup } from '@/hooks/usePatientLookup';
import { formatSSN } from '@/lib/sessionUtils';
import { BloodTestWorkspace } from '@careflux/mercury';
import { TooltipProvider } from '@careflux/primitives';
import { zodResolver } from '@hookform/resolvers/zod';
import { Droplets, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { I18nextProvider } from 'react-i18next';
import {
  errorTextClasses,
  kennitalaFieldClasses,
  lockedPatientErrorClasses,
  lookupErrorClasses,
  lookupStatusRowClasses,
  patientBlockClasses,
  patientKennitalaClasses,
  patientNameClasses,
  sheetBodyClasses,
  sheetContentClasses,
  sheetTitleClasses,
  statusTextClasses,
} from '../bloodTests.styles';
import {
  type BloodTestOrderFormProps,
  type BloodTestOrderSheetProps,
  type KennitalaFormValues,
  type LockedPatientBlockProps,
  type LookupStatusRowProps,
  kennitalaSchema,
} from '../bloodTests.types';
import { IMPERSONATING_MESSAGE, labOrderErrorMessage } from '../labOrderMessages';
import { mercuryI18n } from '../mercuryI18n';
import { useBloodTestOrdering } from '../useBloodTestOrdering';
import { useBloodTestOrderingGate } from '../useBloodTestOrderingGate';
import { BloodTestRequestEditor } from './BloodTestRequestEditor';
import { BloodTestResolutionNotice } from './BloodTestResolutionNotice';

export const BloodTestOrderSheet = ({ task, onClose, onOrdered }: BloodTestOrderSheetProps) => (
  <Sheet open={task !== null} onOpenChange={(open) => !open && onClose()}>
    <SheetContent side="center" className={sheetContentClasses}>
      <SheetHeader className="mb-4">
        <SheetTitle className={sheetTitleClasses}>
          <Droplets className="h-5 w-5 text-primary" />
          Blóðprufubeiðni
        </SheetTitle>
      </SheetHeader>
      {task && <BloodTestOrderBody key={task.todoId} task={task} onClose={onClose} onOrdered={onOrdered} />}
    </SheetContent>
  </Sheet>
);

const BloodTestOrderBody = (props: BloodTestOrderFormProps) => {
  const { user } = useAuth();
  const { orderingEnabled, canOrder } = useBloodTestOrderingGate();
  if (user?.impersonating) return <p className={statusTextClasses}>{IMPERSONATING_MESSAGE}</p>;
  if (canOrder) return <BloodTestOrderForm {...props} />;
  return orderingEnabled ? <LaeknanumerPrompt /> : <BloodTestRequestEditor {...props} />;
};

const LockedPatientBlock = ({ name, ssn }: LockedPatientBlockProps) => (
  <div className={patientBlockClasses}>
    <span className={patientNameClasses}>{name ?? '—'}</span>
    <span className={patientKennitalaClasses}>{formatSSN(ssn)}</span>
  </div>
);

const LookupStatusRow = ({ isFetching, name }: LookupStatusRowProps) => {
  if (isFetching) {
    return (
      <div className={lookupStatusRowClasses}>
        <Loader2 className="h-3 w-3 animate-spin" />
        Leita...
      </div>
    );
  }
  if (name) return <div className={lookupStatusRowClasses}>{name}</div>;
  return <p className={lookupErrorClasses}>Kennitala fannst ekki í Þjóðskrá.</p>;
};

const BloodTestOrderForm = ({ task, onClose, onOrdered }: BloodTestOrderFormProps) => {
  const { catalog, workspaceProps, submitOrder, unresolvedTerms } = useBloodTestOrdering({
    todoId: task.todoId,
    details: task.details,
    extraData: task.extraData,
    onOrdered: () => {
      onOrdered();
      onClose();
    },
  });
  const form = useForm<KennitalaFormValues>({
    resolver: zodResolver(kennitalaSchema),
    defaultValues: { kennitala: normalizeKennitala(task.prefillKennitala) ?? '' },
  });

  // A todo bound to a patient always locks, even if its kennitala is missing
  // or malformed — that never falls back to the free-entry kennitala field.
  const patient = task.patient ?? null;
  const isLocked = patient !== null;
  const lockedSsn = normalizeKennitala(patient?.ssn);
  // In locked mode, only look up a name the caller didn't provide.
  const needsLockedLookup = isLocked && lockedSsn !== undefined && !patient?.name;

  const lookupFormSsn = isLocked ? undefined : normalizeKennitala(form.watch('kennitala'));
  const lookup = usePatientLookup(needsLockedLookup ? lockedSsn : lookupFormSsn);

  const lockedName = patient?.name ?? lookup.data?.name ?? undefined;
  // Confirm stays disabled (isConfirming) until a locked kennitala exists or the lookup returns a name.
  const canConfirm = isLocked ? lockedSsn !== undefined : !!lookup.data?.name;

  const handleConfirm = () => {
    if (isLocked) return lockedSsn ? submitOrder(lockedSsn) : undefined;
    return form.handleSubmit((values) => submitOrder(values.kennitala))();
  };

  if (catalog.isPending) {
    return (
      <p className={statusTextClasses}>
        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
        Sæki blóðprufur...
      </p>
    );
  }
  if (catalog.isError) return <p className={errorTextClasses}>{labOrderErrorMessage(catalog.error)}</p>;

  return (
    <div className={sheetBodyClasses}>
      {isLocked ? (
        lockedSsn ? (
          <LockedPatientBlock name={lockedName} ssn={lockedSsn} />
        ) : (
          <p className={lockedPatientErrorClasses}>
            Kennitala sjúklings er ógild eða vantar. Ekki er hægt að panta fyrir þennan sjúkling.
          </p>
        )
      ) : (
        <Form {...form}>
          <form onSubmit={(event) => event.preventDefault()} className={kennitalaFieldClasses}>
            <FormField
              control={form.control}
              name="kennitala"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Kennitala sjúklings</FormLabel>
                  <FormControl>
                    <Input {...field} inputMode="numeric" autoComplete="off" maxLength={10} />
                  </FormControl>
                  <FormMessage />
                  {lookupFormSsn && <LookupStatusRow isFetching={lookup.isFetching} name={lookup.data?.name} />}
                </FormItem>
              )}
            />
          </form>
        </Form>
      )}
      <BloodTestResolutionNotice unresolvedTerms={unresolvedTerms} />
      <I18nextProvider i18n={mercuryI18n}>
        <TooltipProvider>
          <BloodTestWorkspace
            {...workspaceProps}
            isConfirming={workspaceProps.isConfirming || !canConfirm}
            onConfirm={handleConfirm}
          />
        </TooltipProvider>
      </I18nextProvider>
    </div>
  );
};
